import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Youtube, RefreshCw, Loader2, Unplug, CheckCircle2, AlertTriangle } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";

export const AdminYouTube = ({ readOnly = false, onSynced }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/youtube/status`, { headers: authHeaders() })
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/youtube/oauth/start`, { headers: authHeaders() });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not start the YouTube connection.");
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/youtube/sync`, {}, { headers: authHeaders() });
      toast.success("Sync started — pulling your channel stats from YouTube.");
      const startedAt = Date.now();
      clearInterval(pollRef.current);
      pollRef.current = setInterval(() => {
        axios.get(`${API}/youtube/status`, { headers: authHeaders() }).then((r) => {
          setStatus(r.data);
          const done = r.data.last_sync && Date.now() - startedAt > 2500;
          if (done || Date.now() - startedAt > 90000) {
            clearInterval(pollRef.current);
            setBusy(false);
            if (r.data.last_sync_status === "ok") {
              toast.success(`Sync complete — ${r.data.synced_videos} videos updated.`);
              onSynced?.();
            } else if (r.data.last_sync_status === "error") {
              toast.error(`Sync failed: ${r.data.last_sync_error || "unknown error"}`);
            }
          }
        }).catch(() => {});
      }, 3000);
    } catch (err) {
      setBusy(false);
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not start sync.");
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/youtube/disconnect`, {}, { headers: authHeaders() });
      toast.success("YouTube disconnected. Synced stats stay in the library.");
      load();
    } catch {
      toast.error("Could not disconnect.");
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <div data-testid="youtube-connect-card" className="mt-5 rounded-3xl border border-rose-500/20 bg-[var(--pv-card)] p-5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-rose-500/15 text-rose-400">
          <Youtube className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-bold text-[var(--pv-text)]">YouTube channel sync</p>
          <p data-testid="youtube-status-text" className="mt-0.5 text-xs text-[var(--pv-text-4)]">
            {status.demo && "Demo mode — the YouTube connection is disabled here; any video data in this view is synthetic."}
            {!status.demo && !status.configured && "Server OAuth credentials not configured yet — ask the owner to finish the Google setup."}
            {!status.demo && status.configured && !status.connected && "Not connected — connect the @ItsMelto channel to auto-sync views, revenue and retention."}
            {status.connected && (
              <>
                Connected{status.channel_title ? ` as ${status.channel_title}` : ""} · {status.synced_videos} video(s) synced
                {status.last_sync && <> · last sync {new Date(status.last_sync).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</>}
                {status.monetary_available === false && <> · revenue metrics unavailable (channel not monetized or scope missing)</>}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {status.connected ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-400" data-testid="youtube-connected-icon" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-amber-400" data-testid="youtube-disconnected-icon" />
          )}
          {!readOnly && status.configured && !status.connected && (
            <button data-testid="youtube-connect-btn" onClick={connect} disabled={busy} className="flex items-center gap-1.5 rounded-full bg-rose-500 px-4 py-2 text-xs font-bold text-white hover:bg-rose-600 disabled:opacity-60">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Youtube className="h-3.5 w-3.5" />} Connect YouTube
            </button>
          )}
          {!readOnly && status.connected && (
            <>
              <button data-testid="youtube-sync-btn" onClick={sync} disabled={busy} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-60">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Sync now
              </button>
              <button data-testid="youtube-disconnect-btn" onClick={disconnect} disabled={busy} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3.5 py-2 text-xs font-bold text-[var(--pv-text-3)] hover:text-rose-400 disabled:opacity-60">
                <Unplug className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
